import { AttrField, BooleanField, NumberField, OrmModel, PrimaryKey, StringField } from 'vuex-orm-decorators'
import { modelNames } from '@/data/models/types'
import BaseModel from '@/data/models/BaseModel'
import {
  ChatDirection,
  ChatType,
  type MessengerMessageType,
  type TMessageItem,
  type ChatMessagePayload,
} from '@/data/models/domain/chats/types'
import type { SmsRejectReason, SmsStatus } from '@/types'
import type AttachmentFile from '@/data/models/domain/attachmentFile/AttachmentFile'
import { isExternalMessenger } from '@/utils/smsInfo'

@OrmModel(modelNames.CHAT_MESSAGE)
export default class ChatMessage<Payload extends ChatMessagePayload = ChatMessagePayload> extends BaseModel {
  /**
   * unique composite (fake) key: direction + id
   * due to the fact that we have different ID sequences for incoming, outgoing messages and calls,
   * we can get the same IDs for different types of chat messages
   * this key is created by static method getUid
   */
  @PrimaryKey()
  @StringField()
  public uid: string

  /**
   * possibly non-unique identifier for incoming, outgoing messages and calls
   * FOR USE in requests to the server
   */
  @StringField()
  public id: string

  @AttrField(undefined)
  public chatId: string | undefined

  @AttrField(null)
  public avatar: string | null

  @StringField()
  public direction: ChatDirection

  @AttrField()
  public firstName: string | null

  @AttrField()
  public lastName: string | null

  @StringField()
  public messageTime: string

  @StringField(null)
  public updatedAt: string | null

  @AttrField(null)
  public receiver: string | null

  @AttrField(null)
  public sender: string | null

  @NumberField()
  public sessionId: number

  @StringField()
  public status: SmsStatus

  @StringField()
  public rejectReason: SmsRejectReason

  @StringField()
  public errorCode: string

  @StringField()
  public text: string

  @AttrField()
  public msgItems: Array<TMessageItem>

  @StringField()
  public price: string

  @AttrField(null, (t: unknown) => {
    if (typeof t === 'number') {
      return t.toString()
    }
    return t
  })
  public initiatorId: string | null

  @StringField(null)
  public createdByAutomationId: string | null

  /**
   * to distinguish SMS messages from WhatsApp messages
   */
  @StringField()
  public chatType: ChatType

  @StringField()
  public type: MessengerMessageType | null

  @AttrField(null)
  public payload: Payload | null

  @AttrField([])
  public files: AttachmentFile[]

  @BooleanField(null)
  public isEdited: boolean | null

  public static getUid(id: string, direction: ChatDirection, chatType: ChatType, isNote: boolean): string {
    if (chatType === ChatType.SMS) {
      let dir: string = direction
      if (ChatMessage.VoiceCallValues.has(direction)) {
        dir = 'c'
      }
      return `${dir}${id}`
    }
    // IDs of messages and notes may overlap, because they are different entities on the server
    if (isNote && isExternalMessenger(chatType)) {
      return `note${id}`
    }
    return id
  }

  public static VoiceCallValues = new Set([
    ChatDirection.CallInbound,
    ChatDirection.CallOutbound,
    ChatDirection.CallMissedInbound,
  ])
}
